import { z } from 'zod'

/**
 * Zod input/output schemas for every K4ia tool (see SPEC "Aktionen").
 * The Anthropic `input_schema` sent to the model is derived from these via
 * `zodToJsonSchema` in packages/core so there is a single source of truth.
 */

export const searchKnowledgeInputSchema = z.object({
  query: z.string().min(1),
  showcaseId: z.string().optional(),
  limit: z.number().int().min(1).max(10).default(5),
})
export const searchKnowledgeOutputSchema = z.object({
  results: z.array(
    z.object({
      documentId: z.string(),
      documentTitle: z.string(),
      chunkId: z.string(),
      text: z.string(),
      score: z.number(),
    }),
  ),
})

export const getShowcaseInputSchema = z.object({
  showcaseIdOrSlug: z.string().min(1),
})
export const getShowcaseOutputSchema = z.object({
  found: z.boolean(),
  showcase: z.record(z.unknown()).optional(),
  /** Bilder, Videos, PDFs des Showcases — zum Zeigen mit show_media. Ohne Referenzfotos. */
  media: z
    .array(
      z.object({
        assetId: z.string(),
        type: z.enum(['image', 'video', 'pdf', 'reference_image']),
        description: z.string(),
        language: z.string(),
      }),
    )
    .optional(),
})

export const showMediaInputSchema = z.object({
  assetId: z.string().min(1),
  caption: z.string().optional(),
})
export const showMediaOutputSchema = z.object({
  assetId: z.string(),
  url: z.string(),
  type: z.enum(['image', 'video', 'pdf', 'reference_image']),
})

export const startTourInputSchema = z.object({
  interests: z.array(z.string()).default([]),
})
export const startTourOutputSchema = z.object({
  showcaseIds: z.array(z.string()),
  reasoning: z.string(),
})

/**
 * SPEC "Quiz am Ende": three short questions about what the group just saw.
 * The tool only supplies the *material* from the visited showcases — K4ia
 * asks the questions herself, one at a time, so it stays a conversation
 * rather than a rigid form.
 */
export const startQuizInputSchema = z.object({
  /** Empty = quiz over everything visited so far. */
  showcaseIds: z.array(z.string()).default([]),
  questionCount: z.number().int().min(1).max(5).default(3),
})
export const startQuizOutputSchema = z.object({
  material: z.array(
    z.object({
      showcaseId: z.string(),
      title: z.string(),
      facts: z.array(z.string()),
    }),
  ),
  questionCount: z.number().int(),
  /** Set when the visitor has not seen anything yet — K4ia should offer a tour instead. */
  note: z.string().optional(),
})

export const callStaffInputSchema = z.object({
  showcaseId: z.string().optional(),
  reason: z.string().min(1),
})
export const callStaffOutputSchema = z.object({
  notified: z.boolean(),
  channel: z.string(),
})

export const captureLeadInputSchema = z.object({
  name: z.string().min(1),
  company: z.string().optional(),
  email: z.string().email(),
  notes: z.string().optional(),
})
export const captureLeadOutputSchema = z.object({
  leadId: z.string(),
  pipedriveId: z.string().nullable(),
})

export const bookMeetingInputSchema = z.object({
  attendeeEmail: z.string().email(),
  attendeeName: z.string(),
  proposedStartIso: z.string(),
  durationMinutes: z.number().int().min(15).max(180).default(30),
  subject: z.string(),
})
export const bookMeetingOutputSchema = z.object({
  booked: z.boolean(),
  eventId: z.string().nullable(),
  alternativeSlots: z.array(z.string()).optional(),
})

export const sendSummaryInputSchema = z.object({
  email: z.string().email(),
  // Deliberately NO sessionId: the summary link lets its holder continue that
  // conversation, so which session is summarized must never be something the
  // model can name — the tool takes it from the session context instead.
})
export const sendSummaryOutputSchema = z.object({
  sent: z.boolean(),
  summaryUrl: z.string(),
})

/**
 * `create_followup`: Ein Besucher hat Fragen, die K4ia nicht beantworten kann,
 * oder will, dass sich jemand meldet. Daraus wird eine Aufgabe im
 * Projektwerkzeug des Teams (Smenso, Projekt „K4ia") mit allem, was das Team
 * zum Zurückrufen braucht. Telefon **oder** Mail muss dabei sein — sonst kann
 * sich niemand melden.
 */
export const createFollowupInputSchema = z
  .object({
    questions: z
      .array(z.string().min(1))
      .min(1)
      .describe(
        'Die offenen Fragen oder Anliegen des Besuchers, jede einzeln und in seinen Worten',
      ),
    name: z.string().optional().describe('Name des Besuchers, falls er ihn schon genannt hat'),
    company: z.string().optional().describe('Firma des Besuchers'),
    phone: z.string().optional().describe('Telefonnummer, wie der Besucher sie genannt hat'),
    email: z.string().email().optional().describe('E-Mail-Adresse'),
    preferredContact: z
      .enum(['phone', 'email', 'any'])
      .optional()
      .describe('Wie der Besucher am liebsten kontaktiert werden möchte'),
    preferredTime: z
      .string()
      .optional()
      .describe('Wann der Besucher erreichbar ist, z. B. „vormittags" oder „ab Montag"'),
    showcaseIds: z
      .array(z.string())
      .optional()
      .describe('IDs oder Slugs der Showcases, um die es geht'),
    notes: z.string().optional().describe('Was das Team sonst wissen sollte'),
  })
  // Beim Aufruf durch das Modell dürfen Kontaktdaten fehlen: Die Kontaktkarte im
  // Chat holt sie ein, bevor die Aufgabe entsteht. Geprüft wird beim Ausführen
  // (`followupContactMissing`), nicht beim Aufruf.
  .describe('Rückfrage ans Team')

/**
 * Was für eine Rückfrage noch fehlt, damit sich jemand melden kann: der Name
 * und Telefon **oder** Mail. Leer heißt vollständig. Dieselbe Prüfung hält im
 * Browser den Knopf grau und verhindert im Werkzeug eine Aufgabe ohne Kontakt.
 */
export function followupContactMissing(input: {
  name?: string | undefined
  phone?: string | undefined
  email?: string | undefined
}): string[] {
  const fehlt: string[] = []
  if (!input.name?.trim()) fehlt.push('Name')
  if (!input.phone?.trim() && !input.email?.trim()) fehlt.push('Telefon oder E-Mail')
  return fehlt
}

export const createFollowupOutputSchema = z.object({
  created: z.boolean(),
  /** Ticket- oder Aufgaben-Kennung des Projektwerkzeugs, sofern es eine liefert. */
  reference: z.string().nullable(),
  /** Welches System die Aufgabe hält („smenso" oder „mock"). */
  tracker: z.string(),
  /** Wie viele Ansprechpartner per Teams-Direktnachricht informiert wurden. */
  contactsNotified: z.number().int().nonnegative().default(0),
  /** Ob der Besucher eine Eingangsbestätigung per Mail bekommen hat. */
  confirmationMailSent: z.boolean().default(false),
})

/** Tools that act on the outside world and require explicit visitor confirmation before executing. */
export const TOOLS_REQUIRING_CONFIRMATION = [
  'capture_lead',
  'book_meeting',
  'send_summary',
  'call_staff',
  'create_followup',
] as const

// ---------------------------------------------------------------------------
// Preisschilder (ESL-Preisdienst, docs/SETUP.md §8d)
//
// Die elektronischen Preisschilder im Lab hängen am ESL-Preisdienst von
// Nagarro (Collection „J4P“, 47 Artikel). `get_prices` darf jeder — was etwas
// kostet, ist keine geheime Auskunft. Alles, was Preise ändert, ist
// `teamOnly` (packages/core registry): Die Besucher-App ist offen, und
// „mach alles 90 % günstiger“ darf kein Gast sagen können (Flo, 10.10.).
// ---------------------------------------------------------------------------

/** Welche Artikel gemeint sind — genau eine der drei Angaben (`eslAuswahlPruefen`). */
export const eslSelectionFields = {
  items: z
    .array(z.string().trim().min(1))
    .min(1)
    .optional()
    .describe('Genau diese Artikelnummern, z. B. ["FA013", "FA014"]'),
  categories: z
    .array(z.string().trim().min(1))
    .min(1)
    .optional()
    .describe('Alle Artikel dieser Kategorien, z. B. ["Fashion"] oder ["Essen & Getränke"]'),
  all: z.boolean().optional().describe('true = alle Artikel der Collection (alle 47)'),
}

/** Fehlertext, wenn nicht genau eine Auswahl gesetzt ist — sonst `null`. */
export function eslAuswahlPruefen(sel: {
  items?: string[] | undefined
  categories?: string[] | undefined
  all?: boolean | undefined
}): string | null {
  const gesetzt = [sel.items !== undefined, sel.categories !== undefined, sel.all === true].filter(
    Boolean,
  ).length
  if (gesetzt === 1) return null
  return gesetzt === 0
    ? 'Es fehlt die Auswahl: items, categories oder all: true.'
    : 'Nur eine Auswahl auf einmal: items, categories oder all: true.'
}

const eslAuswahlRefine = (
  sel: {
    items?: string[] | undefined
    categories?: string[] | undefined
    all?: boolean | undefined
  },
  ctx: z.RefinementCtx,
) => {
  const fehler = eslAuswahlPruefen(sel)
  if (fehler) ctx.addIssue({ code: z.ZodIssueCode.custom, message: fehler })
}

export const ESL_ROUNDINGS = ['none', '0.05', '0.10', 'end99'] as const
export const ESL_FLASH_COLORS = [
  'RED',
  'GREEN',
  'BLUE',
  'YELLOW',
  'WHITE',
  'CYAN',
  'MAGENTA',
] as const

/** Dauer wie der Preisdienst sie versteht: `30m`, `2h`, `7d`, `1w`, kombinierbar `1d12h`. */
export const ESL_DURATION_RE = /^(\d+[smhdw])+$/

export const getPricesInputSchema = z.object({
  itemIds: z
    .array(z.string().trim().min(1))
    .optional()
    .describe('Nur diese Artikelnummern, z. B. ["FA013"]'),
  category: z
    .string()
    .trim()
    .min(1)
    .optional()
    .describe('Nur diese Kategorie, z. B. "Fashion" oder "Essen & Getränke"'),
  query: z
    .string()
    .trim()
    .min(1)
    .optional()
    .describe('Suchwort im Artikelnamen, z. B. "Hoodie", "Botanical" oder "Aperol"'),
  history: z
    .boolean()
    .optional()
    .describe('true = Änderungsverlauf mitliefern (nur bei genau einer Artikelnummer in itemIds)'),
})

export const eslItemSchema = z.object({
  itemId: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  /** Regulärer Preis, fertig formatiert („24,50 €“). */
  baseEur: z.string(),
  /** Was gerade auf dem Schild steht. */
  effectiveEur: z.string(),
  /** Woher der aktuelle Preis kommt („-20 %“, Regelname), sonst null. */
  rule: z.string().nullable(),
  /** Ein Direktpreis ist aktiv … */
  override: z.boolean(),
  /** … und endet dann (Ortszeit), oder null = unbegrenzt / kein Direktpreis. */
  overrideUntil: z.string().nullable(),
  /** Von Preisregeln und Prozent-Änderungen ausgenommen — nur ein Festpreis ändert ihn. */
  excluded: z.boolean(),
  /** Vusion (die Schilder) hat den aktuellen Preis. */
  inSync: z.boolean(),
  lastError: z.string().nullable(),
})

export const eslHistoryEventSchema = z.object({
  at: z.string(),
  kind: z.string(),
  oldEur: z.string().nullable(),
  newEur: z.string().nullable(),
  label: z.string().nullable(),
  detail: z.string().nullable(),
  actor: z.string().nullable(),
  ok: z.boolean(),
})

export const getPricesOutputSchema = z.object({
  /** „esl“ = echter Preisdienst, „mock“ = Attrappe (kein Schlüssel hinterlegt). */
  service: z.string(),
  count: z.number().int().nonnegative(),
  items: z.array(eslItemSchema),
  history: z.array(eslHistoryEventSchema).optional(),
  note: z.string().optional(),
})

export const setPricesInputSchema = z
  .object({
    ...eslSelectionFields,
    mode: z
      .enum(['percent', 'price', 'rule'])
      .describe(
        'percent = Grundpreis um Prozent ändern, price = Festpreis, rule = bestehende Preisregel anwenden',
      ),
    // Keine exklusiven Grenzen (`.gt()`, `.positive()`): Daraus wird im
    // JSON-Schema `exclusiveMinimum: true`, und Anthropic weist das Schema ab —
    // damit kam am 10.10. in keiner Team-Sitzung mehr eine Antwort.
    percent: z
      .number()
      .min(-99.9)
      .max(100)
      .optional()
      .describe(
        'Nur mode "percent": positiv = Rabatt (20 = 20 % günstiger), negativ = Aufschlag (-10 = 10 % teurer)',
      ),
    price: z
      .union([z.number().min(0.01), z.string().trim().min(1)])
      .optional()
      .describe('Nur mode "price": Preis in Euro, z. B. 19.9 oder "19,90"'),
    ruleName: z.string().trim().min(1).optional().describe('Nur mode "rule": Name der Preisregel'),
    duration: z
      .string()
      .trim()
      .regex(ESL_DURATION_RE, 'Dauer wie "30m", "2h", "7d", "1w" oder "1d12h"')
      .optional()
      .describe(
        'Dauer der Aktion, z. B. "30m", "2h", "7d", "1d12h"; danach gilt von selbst wieder der reguläre Preis. Ohne duration und validUntil gilt die Aktion unbegrenzt.',
      ),
    validUntil: z
      .string()
      .trim()
      .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/, 'Ende wie "2026-10-10T22:00"')
      .optional()
      .describe('Alternativ zu duration: festes Ende in Ortszeit, z. B. "2026-10-10T22:00"'),
    rounding: z
      .enum(ESL_ROUNDINGS)
      .optional()
      .describe(
        'Rundung des neuen Preises: "0.05" (5 Cent), "0.10" (10 Cent), "end99" (auf x,99 abrunden)',
      ),
    permanent: z
      .boolean()
      .optional()
      .describe(
        'Nur mode "price": setzt einen neuen Grundpreis für immer statt einer Aktion. Nur auf ausdrücklichen Wunsch.',
      ),
    dryRun: z
      .boolean()
      .optional()
      .describe('true = nur berechnen und zeigen, nichts ändern und nichts senden'),
  })
  .superRefine((input, ctx) => {
    eslAuswahlRefine(input, ctx)
    const fehlt = (feld: string, bedingung: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} fehlt (${bedingung}).` })
    if (input.mode === 'percent' && input.percent === undefined) fehlt('percent', 'mode "percent"')
    if (input.mode === 'price' && input.price === undefined) fehlt('price', 'mode "price"')
    if (input.mode === 'rule' && !input.ruleName) fehlt('ruleName', 'mode "rule"')
    if (input.permanent && input.mode !== 'price')
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'permanent geht nur mit mode "price".' })
    if (input.permanent && (input.duration || input.validUntil))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'permanent ist dauerhaft — ohne duration und validUntil.',
      })
    if (input.duration && input.validUntil)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Entweder duration oder validUntil.' })
  })

export const eslChangeResultSchema = z.object({
  service: z.string(),
  /** „ok“ = alles geändert, „partial“ = ein Teil, siehe results. */
  status: z.string(),
  dryRun: z.boolean(),
  /** Wie die Aktion heißt („-20 %“, „19,90 €“), falls der Dienst es nennt. */
  label: z.string().nullable(),
  validUntil: z.string().nullable(),
  summary: z.object({
    ok: z.number().int().nonnegative(),
    forbidden: z.number().int().nonnegative(),
    notFound: z.number().int().nonnegative(),
    skippedExcluded: z.number().int().nonnegative(),
  }),
  results: z.array(
    z.object({
      itemId: z.string(),
      name: z.string().nullable(),
      /** ok · forbidden · not_found · skipped_excluded */
      status: z.string(),
      message: z.string(),
      baseEur: z.string().nullable(),
      newEur: z.string().nullable(),
    }),
  ),
  /** Ob die Schilder den Preis schon haben; null, wenn nichts gesendet wurde (dryRun). */
  push: z
    .object({
      sent: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      unchanged: z.number().int().nonnegative(),
      error: z.string().nullable(),
    })
    .nullable(),
  note: z.string().optional(),
})

export const clearPricesInputSchema = z
  .object({ ...eslSelectionFields })
  .superRefine(eslAuswahlRefine)

export const flashLabelInputSchema = z.object({
  itemId: z.string().trim().min(1).describe('Artikelnummer, z. B. "FA013"'),
  color: z.enum(ESL_FLASH_COLORS).optional().describe('Farbe, Vorgabe GREEN'),
  seconds: z.number().int().min(1).max(60).optional().describe('Dauer in Sekunden, Vorgabe 15'),
})
export const flashLabelOutputSchema = z.object({
  service: z.string(),
  itemId: z.string(),
  ok: z.boolean(),
  message: z.string().nullable(),
  note: z.string().optional(),
})

export const updateItemsInputSchema = z
  .object({
    items: z.array(z.string().trim().min(1)).min(1).describe('Artikelnummern'),
    category: z.string().trim().min(1).optional().describe('Neue Kategorie'),
    excluded: z
      .boolean()
      .optional()
      .describe(
        'true = von Preisregeln und Prozent-Änderungen ausnehmen, false = Ausnahme aufheben',
      ),
    name: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe('Neuer Artikelname — nur bei genau einer Artikelnummer'),
  })
  .superRefine((input, ctx) => {
    if (input.category === undefined && input.excluded === undefined && input.name === undefined)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nichts zu ändern: category, excluded oder name angeben.',
      })
    if (input.name !== undefined && input.items.length !== 1)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Ein Name gilt für genau einen Artikel.',
      })
  })
export const updateItemsOutputSchema = z.object({
  service: z.string(),
  results: z.array(
    z.object({
      itemId: z.string(),
      name: z.string().nullable(),
      status: z.string(),
      message: z.string(),
    }),
  ),
  note: z.string().optional(),
})

export const pushPricesInputSchema = z.object({})
export const pushPricesOutputSchema = z.object({
  service: z.string(),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  error: z.string().nullable(),
  note: z.string().optional(),
})

/** Werkzeuge, die nur dem Team zur Verfügung stehen (`teamOnly` in der Registry). */
export const TEAM_ONLY_TOOLS = [
  'set_prices',
  'clear_prices',
  'flash_label',
  'update_items',
  'push_prices',
] as const
